import type {
  ArtworkFinding,
  FindingTier,
} from "@shared-core/data/customer-link-adapter";
import { Card } from "../../app/components/primitives/card";
import { cn } from "../../app/components/primitives/utils";
import { CheckCircle2, Info, XCircle } from "lucide-react";

// A file's check result on a quote or order line, or a proof's: the three
// customer tiers in their fixed order, "We can't print this file" first and
// never buried, then "We'll sort this for you", then "Worth knowing" (doc 39
// §6), each said once, in one box (Ben, 5 Oct). The findings come worded by
// the backend (doc 66 §6.6 item 1): the page only groups them by tier.

const GROUPS: Array<{
  tier: FindingTier;
  title: string;
  icon: typeof XCircle;
  box: string;
  iconClass: string;
}> = [
  {
    tier: "cant_print",
    title: "We can't print this file",
    icon: XCircle,
    box: "border-red-300 bg-red-50 dark:border-red-900 dark:bg-red-950/30",
    iconClass: "text-red-600 dark:text-red-400",
  },
  {
    tier: "we_will_sort",
    title: "We'll sort this for you",
    icon: CheckCircle2,
    box: "border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/20",
    iconClass: "text-green-700 dark:text-green-400",
  },
  {
    tier: "worth_knowing",
    title: "Worth knowing",
    icon: Info,
    box: "border-muted bg-muted/20",
    iconClass: "text-muted-foreground",
  },
];

export function ArtworkCheckIssueGroups({
  findings,
}: {
  findings: ArtworkFinding[];
}) {
  return (
    <>
      {GROUPS.map(({ tier, title, icon: Icon, box, iconClass }) => {
        const shown = findings.filter((f) => f.tier === tier);
        if (shown.length === 0) return null;
        return (
          <Card key={tier} className={cn("gap-2 border p-3", box)}>
            <div className="flex items-center gap-2">
              <Icon className={cn("size-5 shrink-0", iconClass)} />
              <p className="text-sm font-medium">{title}</p>
            </div>
            <ul className="space-y-2">
              {shown.map((f) => (
                <li
                  key={f.id}
                  className="border-b border-muted pb-2 text-sm last:border-0 last:pb-0"
                >
                  {f.sentence}
                </li>
              ))}
            </ul>
          </Card>
        );
      })}
    </>
  );
}
